/**
 * Deal Meetings Section - Embedded in deal detail pages
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMeetings } from '@/hooks/useMeetings';
import { MEETING_TRANSLATIONS } from '@/constants/meeting.translations';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import CreateMeetingDialog from './CreateMeetingDialog';
import { Video, Calendar, Play, Clock, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';

interface DealMeetingsSectionProps {
  dealId?: string;
  dealName?: string;
}

export function DealMeetingsSection({ dealId, dealName }: DealMeetingsSectionProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = MEETING_TRANSLATIONS[language as 'en' | 'fr'] || MEETING_TRANSLATIONS.en;
  const dateLocale = language === 'fr' ? fr : enUS;
  
  const { meetings, isLoading, loadMeetings } = useMeetings(user?.id || '');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    // Load all meetings if no dealId, or filter by dealId if provided
    loadMeetings(dealId);
  }, [dealId, loadMeetings]);

  const scheduledMeetings = meetings.filter(m => m.status === 'scheduled');
  const activeMeetings = meetings.filter(m => m.status === 'active');
  const pastMeetings = meetings.filter(m => m.status === 'ended');

  const handleJoinMeeting = (meetingId: string) => {
    navigate(`/meetings/${meetingId}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 text-white animate-pulse">{t.active}</Badge>;
      case 'scheduled':
        return <Badge variant="secondary">{t.scheduled}</Badge>;
      case 'ended':
        return <Badge variant="outline">{t.ended}</Badge>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Video className="h-5 w-5 text-primary" />
              {language === 'fr' ? 'Réunions' : 'Meetings'}
            </CardTitle>
            <Button size="sm" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              {t.createMeeting}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Active Meetings */}
          {activeMeetings.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-green-600 flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                {language === 'fr' ? 'En cours' : 'Active'}
              </h4>
              {activeMeetings.map(meeting => (
                <div
                  key={meeting.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                >
                  <div className="flex items-center gap-3">
                    <Video className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium">{meeting.title}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {meeting.startedAt && format(meeting.startedAt, 'HH:mm', { locale: dateLocale })}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleJoinMeeting(meeting.id)} className="bg-green-600 hover:bg-green-700">
                    <Play className="h-4 w-4 mr-1" />
                    {t.joinMeeting}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Scheduled Meetings */}
          {scheduledMeetings.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {language === 'fr' ? 'Planifiées' : 'Scheduled'}
              </h4>
              {scheduledMeetings.map(meeting => (
                <div
                  key={meeting.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{meeting.title}</p>
                      {meeting.scheduledAt && (
                        <p className="text-xs text-muted-foreground">
                          {format(meeting.scheduledAt, 'PPp', { locale: dateLocale })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(meeting.status)}
                    <Button variant="outline" size="sm" onClick={() => handleJoinMeeting(meeting.id)}>
                      {t.joinMeeting}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Past Meetings */}
          {pastMeetings.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {language === 'fr' ? 'Passées' : 'Past'}
              </h4>
              {pastMeetings.slice(0, 3).map(meeting => (
                <div
                  key={meeting.id}
                  className="flex items-center justify-between p-3 rounded-lg border opacity-75"
                >
                  <div className="flex items-center gap-3">
                    <Video className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{meeting.title}</p>
                      {meeting.endedAt && (
                        <p className="text-xs text-muted-foreground">
                          {format(meeting.endedAt, 'PP', { locale: dateLocale })}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleJoinMeeting(meeting.id)}>
                    {t.viewTranscription}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {meetings.length === 0 && (
            <div className="text-center py-6">
              <Video className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">{language === 'fr' ? 'Aucune réunion' : 'No meetings'}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {language === 'fr' ? 'Créez votre première réunion' : 'Create your first meeting'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateMeetingDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onMeetingCreated={() => loadMeetings(dealId)}
        userId={user?.id || ''}
        defaultDealId={dealId}
      />
    </>
  );
}
